import React, { createRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { HELP_URL } from 'Utilities/urls';

import { Content } from 'LegacySBCRA/Content/Content';

import Step1 from '../../../../../client/assets_seller/howitworks/step_1';
import Step2 from '../../../../../client/assets_seller/howitworks/step_2';
import Step3 from '../../../../../client/assets_seller/howitworks/step_3';
import Step4 from '../../../../../client/assets_seller/howitworks/step_4';
import StepComplete from '../../../../../client/assets_seller/howitworks/step_complete';
import { useBreakpoint } from '../../../../components/misc/Breakpoints/Breakpoints';
import { SvgAnimationBox } from '../../../../components/svgAnimationBox/SvgAnimationBox';

import Texts from './HowItWorks.json';

import styles from './HowItWorks.scss';

const LocalT = new MDText(Texts);

const images = [
  {
    id: 'anLetterStart',
    svg: Step1,
  },
  {
    id: 'anSoundWaveStart',
    svg: Step2,
  },
  {
    id: 'anBubbleUpStart',
    svg: Step3,
  },
  {
    id: 'anEyeOpenStart',
    svg: Step4,
  },
  {
    id: 'asStepComplete',
    svg: StepComplete,
  },
];

const HowItWorks = () => {
  const breakpoints = useBreakpoint();
  const { breakpointMobile } = breakpoints.maxWidth;
  const [curAnimate, setCurAnimate] = useState(false);
  const [imageRefs] = useState(
    Array.from({ length: images.length }, () => createRef()),
  );

  const setVisibleandPos = (el) => {
    let lastVis = null;
    imageRefs.forEach(({ current }, i) => {
      if (el.target.isEqualNode(current)) {
        if (el.isIntersecting) {
          lastVis = i + 1;
        }
      }
    });
    if (lastVis) {
      setCurAnimate(lastVis);
    }
  };

  useEffect(() => {
    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((el) => {
            setVisibleandPos(el);
          });
        },
        { threshold: breakpointMobile ? 0.4 : 0.8 },
      );
      imageRefs.forEach((it) => {
        observer.observe(it.current);
      });
    }

    return () => {
      observer?.disconnect();
    };
  }, [imageRefs, breakpointMobile]);

  const fetchLearnMoreLink = (index) => {
    const ARTICLES_PATH = `${HELP_URL}/articles`;

    let href = HELP_URL;

    switch (index) {
      case 1:
        href = `${ARTICLES_PATH}/4853683845660-How-do-I-get-an-estimated-sale-price`;
        break;
      case 2:
        href = `${ARTICLES_PATH}/4853722160924-What-is-profiling-How-do-I-profile-my-car`;
        break;
      case 3:
        href = `${ARTICLES_PATH}/4853684956956-What-is-a-reserve-price`;
        break;
      case 4:
        href = `${ARTICLES_PATH}/4853669944220-How-do-you-find-my-highest-offer`;
        break;
      case 5:
        href = `${ARTICLES_PATH}/4853816103452-How-and-when-will-I-be-paid-for-my-vehicle`;
        break;
      default:
        break;
    }

    return (
      <a
        className="mw-link mw-link-blue"
        href={href}
        rel="noopener noreferrer"
        target="_blank"
      >
        {LocalT.translate('learnMore')}
      </a>
    );
  };

  return (
    <div className={styles.view}>
      <div className={styles.topWrapper}>
        <Content
          center
          className={styles.hero}
          wrapper={{ className: styles.contentWrapper }}
        >
          <div className={styles.content}>
            <h1 className={styles.title}>{LocalT.translate('hero.title')}</h1>
            <h4 className={styles.subTitle}>{LocalT.translate('hero.copy')}</h4>
          </div>
        </Content>
      </div>

      {images.map(({ id, svg }, i) => {
        const step = i + 1;

        return (
          <div
            key={step}
            ref={imageRefs[i]}
            className={styles.contentBlockWrapper}
          >
            <Content
              center
              className={cx(styles.section, styles[`step${step}`])}
              element="section"
            >
              <div className={cx(styles.content, styles.card)}>
                <span className={styles.index}>{step}</span>
                <h2>{LocalT.translate(`step${step}.title`)}</h2>
                <p>
                  {LocalT.translate(`step${step}.p1`, {
                    link: fetchLearnMoreLink(step),
                  })}
                </p>
                { step === 1 && <p className={styles.disclaimer}>{LocalT.translate('step1.disclaimer')} <Link className='mw-link-blue' to='/terms'>See terms</Link></p> }
              </div>
              <div className={styles.image}>
                <SvgAnimationBox
                  animate={curAnimate === step}
                  className={styles.innerSvg}
                  {...{ id, svg }}
                />
              </div>
            </Content>
          </div>
        );
      })}

      <Content className={styles.pageTitle}>
        <h2>{LocalT.translate('pageTitle')}</h2>
      </Content>
    </div>
  );
};

export default HowItWorks;
